import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss']
})
export class ImageCropperComponent implements OnChanges {
  @Input() imageFile?: File;
  @Input() type?: number;
  @Input() cropperMinWidth: number = 0;
  @Input() cropperMinHeight: number = 0;
  @Input() templateSlug?: string = '';
  @Output() croppedImage = new EventEmitter<any>();
  imageChangedEvent: any;
  scale = 1;
  transform: ImageTransform = {};
  maintainAspectRatio: boolean = true;
  cropperStaticWidth: number = 0;
  cropperStaticHeight: number = 0;
  hideResizeSquares: boolean = false;
  onlyScaleDown: boolean = false;
  aspectRatio: number = 1 / 1;
  resizeToWidth: number = 0;
  resizeToHeight: number = 0;

  ngOnChanges() {
    if (this.imageFile) {
      // Drop previous cropper image immediately — old event stayed until FileReader finished.
      this.imageChangedEvent = null;
      this.resetImage();
      this.cropperStaticWidth = 0;
      this.cropperStaticHeight = 0;
      this.hideResizeSquares = false;
      this.resizeToWidth = 0;
      this.resizeToHeight = 0;
      if (this.type == 1) {
        this.cropperMinWidth = 175;
        this.cropperMinHeight = 175;
        this.maintainAspectRatio = true;
        this.aspectRatio = 1;
        this.onlyScaleDown = false;
        // Consistent square output for circular card avatars (card-add + bulk-update).
        this.resizeToWidth = 400;
        this.resizeToHeight = 400;
      } else if (this.type == 2) {
        this.cropperMinWidth = 0;
        this.cropperMinHeight = 0;
        this.maintainAspectRatio = false;
      } else if (this.type == 3 || this.type == 5) {
        if (this.templateSlug == 'executive-pro') {
          this.cropperStaticWidth = 400;
          this.cropperStaticHeight = 170;
          this.cropperMinWidth = 400;
          this.cropperMinHeight = 170;
          this.maintainAspectRatio = true;
          this.onlyScaleDown = false;
          this.aspectRatio = 400 / 170;
          this.hideResizeSquares = true;
        } else {
          this.cropperMinWidth = 400;
          this.cropperMinHeight = 220;
          this.maintainAspectRatio = true;
          this.onlyScaleDown = true;
          this.aspectRatio = 400 / 220;
        }
      } else if (this.type == 4) {
        this.cropperMinWidth = 340;
        this.cropperMinHeight = 250;
        this.maintainAspectRatio = true;
        this.onlyScaleDown = true;
        this.aspectRatio = 340 / 250;
      }

      this.readFile(this.imageFile);
      // this.resizeBeforeCrop(this.imageFile);
    }
  }

  readFile(file: File) {
    this.imageChangedEvent = null;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setTimeout(() => {
        this.imageChangedEvent = { target: { files: [file] } };
      });
    };
  }

  imageCropped(event: ImageCroppedEvent) {
    const base64Image = event.base64;
    if (!base64Image) {
      return;
    }
    const dataUrlMatch = /^data:([^;,]+)[;,]/.exec(base64Image);
    const mime = ((dataUrlMatch?.[1] ?? 'image/jpeg') as string).trim().toLowerCase();
    const commaIdx = base64Image.indexOf(',');
    const payload = commaIdx >= 0 ? base64Image.slice(commaIdx + 1) : base64Image;
    const byteString = atob(payload);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mime });
    const ext =
      mime === 'image/jpeg' || mime === 'image/jpg'
        ? 'jpg'
        : mime === 'image/webp'
          ? 'webp'
          : mime === 'image/png'
            ? 'png'
            : 'jpg';
    const orig = this.imageFile!.name;
    const stem = /\.[^/.]+$/.test(orig) ? orig.slice(0, orig.lastIndexOf('.')) : orig;
    const file = new File([blob], `${stem}.${ext}`, { type: mime });
    const objResult = { file: file, type: this.type };
    this.croppedImage.emit(objResult);
  }

  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  resetImage() {
    this.scale = 1;
    this.transform = {};
  }

  resizeBeforeCrop(file: File) {
    // Resize the image to a smaller size before cropping
    // Example code using canvas to resize the image
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      this.imageChangedEvent = null; // clear previous selection
      // Calculate new dimensions based on maxWidth and maxHeight
      let maxWidth = 600;
      let maxHeight = 400;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image onto canvas
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert canvas to Blob
      canvas.toBlob((blob: any) => {
        const resizedFile = new File([blob], file.name, {
          type: 'image/jpeg', // Adjust according to your image type
          lastModified: Date.now()
        });

        // Pass the resized file to the image cropper
        // this.imageChangedEvent = resizedFile;
        this.imageChangedEvent = { target: { files: [resizedFile] } };
      }, 'image/jpeg');
    };

    img.src = URL.createObjectURL(file);
  }
}
